'use client';

import { forwardRef } from 'react';

import { usePressAnimation } from '@shaojiang/hooks/usePressAnimation';

import { Spinner } from './spinner';
import { Button as StyledButton, type ButtonProps as StyledButtonProps } from './styled/button';

interface ButtonLoadingProps {
  loading?: boolean;
  loadingText?: React.ReactNode;
}

export interface ButtonProps extends StyledButtonProps, ButtonLoadingProps {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { loading, disabled, loadingText, children, ...rest } = props;

  const trulyDisabled = loading || disabled;
  const pressRef = usePressAnimation(ref, !!trulyDisabled);

  // `asChild` needs exactly one child element to merge into, so skip the loading slot
  if (rest.asChild) {
    return (
      <StyledButton ref={pressRef} {...rest}>
        {children}
      </StyledButton>
    );
  }

  return (
    <StyledButton disabled={trulyDisabled} ref={pressRef} {...rest}>
      {loading && <ButtonSpinner />}
      {loading && loadingText ? loadingText : children}
    </StyledButton>
  );
});

Button.displayName = 'Button';

const ButtonSpinner = () => (
  <Spinner
    width='1.1em'
    height='1.1em'
    borderWidth='1.5px'
    borderTopColor='currentColor'
    borderRightColor='currentColor'
  />
);
